// src/types.ts
function unixNow() {
  return Math.floor(Date.now() / 1000);
}
function appendDedupe(a, b) {
  if (!b || b.length === 0)
    return a;
  const set = new Set(a);
  for (const item of b) {
    set.add(item);
  }
  return Array.from(set);
}
function throwIfOffline() {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    throw new Error("Offline");
  }
}
function buildUnsignedEvent(pubkey, kind, tags, content, createdAt) {
  return {
    pubkey,
    kind,
    tags,
    content,
    created_at: createdAt
  };
}
// src/provider.ts
import { base64 } from "@scure/base";

// src/constants.ts
var HTTP_AUTH_KIND = 27235;
var LIVE_STREAM = 30311;
var N94_LIVE_STREAM = 1053;
var LIVE_STREAM_KINDS = [LIVE_STREAM, N94_LIVE_STREAM];
var LIVE_STREAM_CHAT = 1311;
var LIVE_STREAM_RAID = 1312;
var LIVE_STREAM_CLIP = 1313;
var GOAL = 9041;
var VIDEO_KIND = 21;
var SHORTS_KIND = 22;
var OLD_VIDEO_KIND = 34235;
var OLD_SHORTS_KIND = 34236;
var StreamState;
((StreamState2) => {
  StreamState2["Live"] = "live";
  StreamState2["Ended"] = "ended";
  StreamState2["Planned"] = "planned";
  StreamState2["VOD"] = "vod";
})(StreamState ||= {});
var DEFAULT_API_URL = "https://api-core.zap.stream/api/v1";

// src/provider.ts
class TimeSync {
  #offset = 0;
  get offset() {
    return this.#offset;
  }
  async syncClock(apiUrl = "https://api-core.zap.stream/api/v1") {
    try {
      const now = Date.now();
      const req = await fetch(`${apiUrl}/time`, {
        signal: AbortSignal.timeout(1000)
      });
      const nowAtServer = (await req.json()).time;
      this.#offset = nowAtServer - now;
    } catch {}
  }
}
var timeSync = new TimeSync;

class NostrStreamProvider {
  name;
  url;
  #signer;
  #wsConnection;
  #metricsCallbacks = new Map;
  #pendingStreams = new Set;
  #isAuthenticated = false;
  #timeSync;
  constructor(name, url, signer, ts) {
    this.name = name;
    this.url = url;
    if (!url.endsWith("/")) {
      this.url = `${url}/`;
    }
    this.#signer = signer;
    this.#timeSync = ts ?? timeSync;
  }
  setSigner(signer) {
    this.#signer = signer;
  }
  async info() {
    const rsp = await this.#getJson("GET", "account");
    return rsp;
  }
  async updateStreamFromEvent(ev, extractFn) {
    const { title, summary, image, tags, contentWarning, goal, gameId, id } = extractFn(ev);
    const props = {
      id,
      title,
      summary,
      image,
      tags: appendDedupe(tags, gameId ? [gameId] : undefined),
      content_warning: contentWarning,
      goal
    };
    await this.updateStream(props);
  }
  async updateStream(props) {
    await this.#getJson("PATCH", "event", props);
    if (props.id) {
      const { id: _, ...rest } = props;
      await this.#getJson("PATCH", "event", rest);
    }
  }
  async topup(amount) {
    const rsp = await this.#getJson("GET", `topup?amount=${amount}`);
    return rsp.pr;
  }
  async withdraw(invoice) {
    return await this.#getJson("POST", `withdraw?invoice=${invoice}`);
  }
  async acceptTos() {
    await this.#getJson("PATCH", "account", {
      accept_tos: true
    });
  }
  async configureNwc(nwcUri) {
    await this.#getJson("PATCH", "account", {
      nwc: nwcUri
    });
  }
  async removeNwc() {
    await this.#getJson("PATCH", "account", {
      remove_nwc: true
    });
  }
  async addForward(name, target) {
    await this.#getJson("POST", "account/forward", {
      name,
      target
    });
  }
  async removeForward(id) {
    await this.#getJson("DELETE", `account/forward/${id}`);
  }
  async prepareClip(id) {
    return await this.#getJson("GET", `clip/${id}`);
  }
  async createClip(id, clipId, start, length) {
    return await this.#getJson("POST", `clip/${id}/${clipId}?start=${start}&length=${length}`);
  }
  async getNotificationsInfo() {
    return await this.#getJson("GET", "notifications/info");
  }
  async subscribeNotifications(req) {
    return await this.#getJson("POST", "notifications/register", req);
  }
  async listStreamerSubscriptions(auth) {
    return await this.#getJson("GET", `notifications?auth=${auth}`);
  }
  async addStreamerSubscription(pubkey) {
    return await this.#getJson("PATCH", `notifications?pubkey=${pubkey}`);
  }
  async removeStreamerSubscription(pubkey) {
    return await this.#getJson("DELETE", `notifications?pubkey=${pubkey}`);
  }
  getTempClipUrl(id, clipId) {
    return `${this.url}clip/${id}/${clipId}`;
  }
  async history(page = 0, pageSize = 20) {
    return await this.#getJson("GET", `history?page=${page}&pageSize=${pageSize}`);
  }
  async streamKeys(page = 0, pageSize = 20) {
    return await this.#getJson("GET", `keys?page=${page}&pageSize=${pageSize}`);
  }
  async createStreamKey(expires) {
    return await this.#getJson("POST", "keys", {
      event: { title: "New stream key, who dis" },
      expires
    });
  }
  async connectWebSocket() {
    if (this.#wsConnection && this.#wsConnection.readyState === WebSocket.OPEN) {
      return;
    }
    const wsUrl = `${this.url.replace(/^https?:/, "wss:").replace(/\/$/, "")}/ws`;
    this.#wsConnection = new WebSocket(wsUrl);
    this.#wsConnection.onopen = async () => {
      try {
        const signer = this.#signer;
        if (signer) {
          const pubkey = await signer.getPubKey();
          const unsigned = buildUnsignedEvent(pubkey, HTTP_AUTH_KIND, [
            ["u", wsUrl],
            ["method", "GET"]
          ], "", unixNow() + Math.floor(this.#timeSync.offset / 1000));
          const token = await signer.sign(unsigned);
          const authMessage = {
            type: "Auth",
            data: { token: base64.encode(new TextEncoder().encode(JSON.stringify(token))) }
          };
          this.#wsConnection?.send(JSON.stringify(authMessage));
        }
      } catch (error) {
        console.error("Failed to authenticate WebSocket:", error);
      }
    };
    this.#wsConnection.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        switch (data.type) {
          case "AuthResponse":
            this.#isAuthenticated = true;
            this.#subscribeToPendingStreams();
            this.#metricsCallbacks.forEach((callback) => {
              callback(data);
            });
            break;
          case "StreamMetrics":
            this.#metricsCallbacks.forEach((callback) => {
              callback(data);
            });
            break;
          case "Error":
            console.error("WebSocket error:", data.error);
            break;
          default:
            this.#metricsCallbacks.forEach((callback) => {
              callback(data);
            });
        }
      } catch (error) {
        console.error("Failed to parse WebSocket message:", error);
      }
    };
    this.#wsConnection.onclose = (event) => {
      if (event.code !== 1000) {
        setTimeout(() => {
          this.connectWebSocket();
        }, 5000);
      }
    };
    this.#wsConnection.onerror = (error) => {
      console.error("WebSocket error:", error);
    };
  }
  subscribeToMetrics(streamId, callback) {
    const subscriptionKey = `metrics_${streamId}`;
    this.#metricsCallbacks.set(subscriptionKey, callback);
    this.connectWebSocket().then(() => {
      if (this.#isAuthenticated && this.#wsConnection && this.#wsConnection.readyState === WebSocket.OPEN) {
        this.#sendSubscription(streamId);
      } else {
        this.#pendingStreams.add(streamId);
      }
    });
  }
  unsubscribeFromMetrics(streamId) {
    const subscriptionKey = `metrics_${streamId}`;
    this.#metricsCallbacks.delete(subscriptionKey);
    if (this.#metricsCallbacks.size === 0 && this.#wsConnection) {
      this.#wsConnection.close();
    }
  }
  closeWebSocket() {
    if (this.#wsConnection) {
      this.#wsConnection.close();
      this.#wsConnection = undefined;
    }
    this.#metricsCallbacks.clear();
    this.#pendingStreams.clear();
    this.#isAuthenticated = false;
  }
  #subscribeToPendingStreams() {
    this.#pendingStreams.forEach((streamId) => {
      this.#sendSubscription(streamId);
    });
    this.#pendingStreams.clear();
  }
  #sendSubscription(streamId) {
    if (this.#wsConnection && this.#wsConnection.readyState === WebSocket.OPEN) {
      this.#wsConnection.send(JSON.stringify({
        type: "SubscribeStream",
        data: {
          stream_id: streamId
        }
      }));
    }
  }
  async#getJson(method, path, body) {
    const signer = this.#signer;
    if (!signer)
      throw new Error("No signer — provide a Signer via the constructor or setSigner()");
    const u = `${this.url}${path}`;
    const pubkey = await signer.getPubKey();
    const unsigned = buildUnsignedEvent(pubkey, HTTP_AUTH_KIND, [
      ["u", u],
      ["method", method]
    ], "", unixNow() + Math.floor(this.#timeSync.offset / 1000));
    const token = await signer.sign(unsigned);
    const rsp = await fetch(u, {
      method,
      body: body ? JSON.stringify(body) : undefined,
      headers: {
        "content-type": "application/json",
        authorization: `Nostr ${base64.encode(new TextEncoder().encode(JSON.stringify(token)))}`
      }
    });
    const json = await rsp.text();
    if (!rsp.ok) {
      throw new Error(json);
    }
    return json.length > 0 ? JSON.parse(json) : {};
  }
}
// src/game-database.ts
class GameDatabase {
  url;
  constructor(url = "https://api-core.zap.stream/api/v1") {
    this.url = url;
  }
  async searchGames(search, limit = 10) {
    const rsp = await fetch(`${this.url}/games/search?q=${encodeURIComponent(search)}&limit=${limit}`);
    if (rsp.ok) {
      const games = await rsp.json();
      return games.map((a) => ({
        ...a,
        cover: a.cover ? {
          ...a.cover,
          url: `https://images.igdb.com/igdb/image/upload/t_cover_big/${a.cover.image_id}.jpg`
        } : undefined,
        genres: [{ id: 0, name: "gaming" }, ...a.genres ?? []]
      }));
    }
    return [];
  }
  async getGame(id) {
    const igId = id.startsWith("igdb:") ? id.split(":")[1] : id;
    const rsp = await fetch(`${this.url}/games/${igId}`);
    if (rsp.ok) {
      const gameInfo = await rsp.json();
      if (gameInfo?.cover) {
        gameInfo.cover.url = `https://images.igdb.com/igdb/image/upload/t_cover_big/${gameInfo.cover.image_id}.jpg`;
      }
      return gameInfo;
    }
    return;
  }
}
// src/utils.ts
function findTag(e, tag) {
  const maybeTag = e?.tags.find((evTag) => evTag[0] === tag);
  return maybeTag?.[1];
}
var gameTagFormat = /^[a-z-]+:[a-z0-9-]+$/i;
function sortStreamTags(tags) {
  const plainTags = tags.filter((a) => Array.isArray(a) ? a[0] === "t" : true).map((a) => Array.isArray(a) ? a[1] : a);
  const regularTags = plainTags.filter((a) => !a.match(gameTagFormat)) ?? [];
  const prefixedTags = plainTags.filter((a) => !regularTags.includes(a));
  return { regularTags, prefixedTags };
}
function extractGameId(tags) {
  const gameId = tags.find((a) => a.match(gameTagFormat));
  return { gameId, gameTag: gameId };
}
function extractStreamInfo(ev) {
  const ret = {
    tags: [],
    streams: []
  };
  if (!ev)
    return ret;
  const host = findTag(ev, "p") ?? ev.pubkey;
  ret.host = host;
  const matchTag = (tag, k, into) => {
    if (tag[0] === k) {
      into(tag[1]);
    }
  };
  for (const t of ev.tags ?? []) {
    matchTag(t, "d", (v) => ret.id = v);
    matchTag(t, "title", (v) => ret.title = v);
    matchTag(t, "summary", (v) => ret.summary = v);
    matchTag(t, "image", (v) => ret.image = v);
    matchTag(t, "thumb", (v) => ret.thumbnail = v);
    matchTag(t, "status", (v) => ret.status = v);
    if (t[0] === "streaming") {
      ret.streams ??= [];
      ret.streams.push(t[1]);
    }
    matchTag(t, "recording", (v) => ret.recording = v);
    matchTag(t, "url", (v) => ret.recording = v);
    matchTag(t, "content-warning", (v) => ret.contentWarning = v);
    matchTag(t, "current_participants", (v) => ret.participants = v);
    matchTag(t, "goal", (v) => ret.goal = v);
    matchTag(t, "starts", (v) => ret.starts = v);
    matchTag(t, "ends", (v) => ret.ends = v);
    matchTag(t, "service", (v) => ret.service = v);
  }
  const { regularTags, prefixedTags } = sortStreamTags(ev.tags ?? []);
  ret.tags = regularTags;
  const { gameId } = extractGameId(prefixedTags);
  ret.gameId = gameId;
  if (ret.streams.length > 0) {
    ret.stream = ret.streams.find((a) => a.includes(".m3u8")) || ret.streams.at(0);
  }
  return ret;
}
export {
  unixNow,
  timeSync,
  throwIfOffline,
  sortStreamTags,
  findTag,
  extractStreamInfo,
  extractGameId,
  buildUnsignedEvent,
  appendDedupe,
  VIDEO_KIND,
  TimeSync,
  StreamState,
  SHORTS_KIND,
  OLD_VIDEO_KIND,
  OLD_SHORTS_KIND,
  NostrStreamProvider,
  N94_LIVE_STREAM,
  LIVE_STREAM_RAID,
  LIVE_STREAM_KINDS,
  LIVE_STREAM_CLIP,
  LIVE_STREAM_CHAT,
  LIVE_STREAM,
  HTTP_AUTH_KIND,
  GameDatabase,
  GOAL,
  DEFAULT_API_URL
};

//# debugId=075AC45AD974200464756E2164756E21
//# sourceMappingURL=index.js.map
